import type { DeliveryGroup } from '@/modules/cart/domain/Cart';
import environment from '@/modules/cart/environments/environment';
import { localizeNameToString } from '@/modules/shared/mappers/localizeNameToString';

import type {
  Breadcrumb,
  Feature,
  ProductDetails,
  Variant
} from '../../domain/ProductDetails';
import type { BreadcrumbDto } from '../dto/BreadcrumbsDto';
import type {
  DataSheetDto,
  HttpProductDetailsDto,
  PropertyValue,
  VariantDTO
} from '../dto/HttpProductDetailsDto';
import type { WarrantiesCustomObjectDto } from '../dto/WarrantiesCustomObjectDto';

const warrantiesDtoToString = (
  warranties: WarrantiesCustomObjectDto
): string => {
  const { items } = warranties.obj.value;
  return items.reduce((acc, item) => {
    const optionsString = item.options
      .map(({ shortDescription }) => shortDescription)
      .join(', ');
    return acc + optionsString;
  }, '');
};

const { LOCALE } = environment;

const propertyValueDtoToPropertyValue = (
  propertyValue: PropertyValue
): string => {
  if (typeof propertyValue === 'string') {
    return /^(\d{4})-(\d{2})-(\d{2})*/.test(propertyValue)
      ? new Date(propertyValue).toLocaleDateString()
      : propertyValue;
  }

  if (typeof propertyValue === 'boolean') return propertyValue ? 'Si' : 'No';

  if ('label' in propertyValue) return propertyValue.label;

  // If is CustomObject there is warranties, take care if there is other CustomObjet on dataSheet
  if ('obj' in propertyValue) return warrantiesDtoToString(propertyValue);

  if (Array.isArray(propertyValue)) return propertyValue.join(', ');

  return propertyValue;
};

const dataSheetDtoToProductFeatures = (dataSheet: DataSheetDto[]): Feature[] =>
  dataSheet.map(({ title, properties }) => ({
    title,
    properties: properties.map(({ name, label, value }) => ({
      name,
      label,
      value: propertyValueDtoToPropertyValue(value)
    }))
  }));

const breadcrumbsDtoToBreadcrumbs = (
  breadcrumbs: BreadcrumbDto[]
): Breadcrumb[] =>
  breadcrumbs
    ? breadcrumbs.map((breadcrumb) => {
        const url = `/${breadcrumb.name['es-CL']}`;
        return {
          label: breadcrumb.name['es-CL'],
          linkUrl: url
        };
      })
    : [];

export const variantDtoToVariant = (
  variant: VariantDTO,
  name: string
): Variant => {
  return {
    id: variant.id,
    sku: variant.sku,
    prices: variant.prices,
    images: variant.images.map(({ url }, i) => ({ url, alt: `${name}-${i}` })),
    warranties: variant.warranties || undefined,
    services: variant.services || undefined,
    features: variant.dataSheet
      ? dataSheetDtoToProductFeatures(variant.dataSheet)
      : [],
    isPreSale: variant.isPreSale,
    attributes: variant.attributes,
    collections: variant.collections,
    sellers: variant.sellers ?? []
  };
};

export const getDeliveryGroupsOptionsBySku = (
  deliveryGroups: DeliveryGroup[],
  sku: string
): DeliveryGroup[] | undefined =>
  deliveryGroups.filter((group) => group.products.includes(sku));

export const httpProductDetailsDtoToProductDetails = (
  productDetails: HttpProductDetailsDto
): ProductDetails => {
  const name =
    productDetails.name && localizeNameToString(productDetails.name, LOCALE);
  const slug =
    productDetails.slug && localizeNameToString(productDetails.slug, LOCALE);

  return {
    id: productDetails.id,
    name,
    slug,
    productType: productDetails.productType,
    isMarketplace: productDetails.isMarketplace,
    averageRating: productDetails.averageRating,
    countRating: productDetails.countRating,
    brand: productDetails.brand,
    breadcrumbs: [
      ...breadcrumbsDtoToBreadcrumbs(productDetails.primaryCategoryBreadcrumb),
      { label: name, linkUrl: '' }
    ],
    sellers: productDetails.sellers,
    description:
      productDetails.description &&
      localizeNameToString(productDetails.description, LOCALE),
    masterVariant: variantDtoToVariant(productDetails.masterVariant, name),
    variants: productDetails.variants?.length
      ? productDetails.variants?.map((variant) =>
          variantDtoToVariant(variant, name)
        )
      : []
  };
};
